import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children"],
  _excluded2 = ["use", "bordered", "$cssStyles", "$fullHeightPanel"],
  _excluded3 = ["panelStyles", "$fullHeightPanel"],
  _excluded4 = ["tokenProps", "children"],
  _excluded5 = ["bordered", "children", "fill", "fullHeightPanel", "moreTabsRenderer", "onSelectedChange", "selected", "TabsList", "use", "Wrapper", "hideBorders", "panelStyles"];
import { cloneElement, Children, PureComponent, isValidElement } from 'react';
import styled, { css, useTheme } from 'foundations-theming/utils';
import I18n from 'I18n';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { memoizedSequence } from '../../utils/functions';
import { HiddenMeasure } from '../../utils/HiddenMeasure';
import { Controllable } from '../Controllable';
import { Dropdown } from '../Dropdown';
import { List, ListItem } from '../List';
import { Section, StyledSection } from '../Section/Section';
import { getSelectedTabBackgroundColor, isContained } from './tabUtils';
import { TabsProvider } from './TabsProvider';
import { useTabBarContext } from '../TabBar/TabBarProvider';
import { deepMerge } from '../../utils/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getActiveTabPanel = (selected, childArray) => {
  for (let i = 0; i < childArray.length; i++) {
    const child = childArray[i];
    // Provide default values for children and tabId so that if either is false they can still be destructured without being undefined
    const {
      children: tabContent = null,
      tabId = null
    } = child && child.props ? child.props : {};
    if (tabId === selected) {
      return tabContent;
    }
  }
  return null;
};

/**
 * @param {HTMLElement} el
 * @returns {number} The precise width of the element's bounding box (`clientWidth` is rounded)
 */
const getElementWidth = el => el.getBoundingClientRect().width;
const getAvailableWidth = (parentEl, tabsEl) => {
  const computedStyles = getComputedStyle(parentEl, null);

  // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722
  if (!computedStyles) return Infinity;
  const innerWidth = getElementWidth(parentEl) - parseFloat(computedStyles.paddingLeft) - parseFloat(computedStyles.paddingRight);
  let availableWidth = innerWidth;

  // Special case: If we're in a horizontal flex container, respect our siblings' space.
  if (computedStyles.display === 'flex' && computedStyles.flexDirection === 'row' && computedStyles.flexWrap === 'nowrap') {
    const childNodes = parentEl.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      const node = childNodes[i];
      if (node !== tabsEl && node instanceof HTMLElement) {
        availableWidth -= getElementWidth(node);
      }
    }
  }
  return availableWidth;
};
const listRightBorderMixin = css(["border-right:", ";"], ({
  fullWidth,
  theme
}) => !fullWidth ? `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}` : '0');
const enclosedBorderMixin = css(["border:", ";", ";"], ({
  theme
}) => `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`, listRightBorderMixin);
const removeBorders = side => {
  if (side === 'left') {
    return css(["border-left:0;border-top-left-radius:0;"]);
  } else if (side === 'right') {
    return css(["border-right:0;border-top-right-radius:0;"]);
  } else if (side === 'both') {
    return css(["border-left:0;border-right:0;border-top-left-radius:0;border-top-right-radius:0;"]);
  }
  return null;
};
// margin-bottom CSS property aligns with the border width
export const StyledList = styled.div.withConfig({
  displayName: "Tabs__StyledList",
  componentId: "sc-1b1rx27-0"
})(["display:", ";width:", ";margin-bottom:", ";border-bottom:", ";", ";", " ", ";"], ({
  use
}) => isContained(use) ? 'inline-flex' : 'flex', ({
  fullWidth
}) => fullWidth && '100%', ({
  theme
}) => `-${theme.borderWidth['100']}`, ({
  bordered,
  theme
}) => `${theme.borderWidth['100']} solid ${bordered ? theme.color['border-core-subtle'] : theme.color['fill-transparent-default']}`, ({
  use,
  theme
}) => {
  if (use === 'header') return css(["padding-left:", ";"], theme.space['1100']);
  if (use === 'enclosed') return css(["border:", " solid ", ";border-top-left-radius:", ";border-top-right-radius:", ";"], theme.borderWidth['100'], theme.color['border-core-subtle'], theme.borderRadius['100'], theme.borderRadius['100']);
  if (use === 'enclosed-shaded') {
    return enclosedBorderMixin;
  }
  if (use === 'toolbar') return listRightBorderMixin;
  return null;
}, ({
  hideBorders
}) => {
  return removeBorders(hideBorders);
}, ({
  $cssStyles
}) => $cssStyles);
function StyledListWithStyles(props) {
  var _tabBarContext$tabBor;
  const {
      children
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const tabBarContext = useTabBarContext();
  const cssStyles = (_tabBarContext$tabBor = tabBarContext === null || tabBarContext === void 0 ? void 0 : tabBarContext.tabBorderStyleOverrides) !== null && _tabBarContext$tabBor !== void 0 ? _tabBarContext$tabBor : {};
  return /*#__PURE__*/_jsx(StyledList, Object.assign({}, rest, {
    $cssStyles: cssStyles,
    children: children
  }));
}
const getTabsPanelStyles = ($fullHeightPanel, cssStyleOverrides = {}) => Object.assign({
  height: $fullHeightPanel ? '100%' : 'auto'
}, cssStyleOverrides);
const TabsPanel = props => {
  const {
      use,
      bordered,
      $cssStyles,
      $fullHeightPanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const theme = useTheme();
  const tabsSectionStyleOverrides = getTabsPanelStyles($fullHeightPanel, $cssStyles);
  return /*#__PURE__*/_jsx(TabsProvider, {
    tabsSectionStyleOverrides: tabsSectionStyleOverrides,
    tabsLinkStyleOverrides: {},
    tabsTabStyleOverrides: {},
    children: /*#__PURE__*/_jsx(Section, Object.assign({}, rest, {
      borderTop: `${theme.borderWidth['100']} solid ${bordered ? theme.color['border-core-subtle'] : theme.color['fill-transparent-default']}`,
      padding: '400',
      backgroundColor: use === 'enclosed-shaded' ? 'fill-transitional-tabs-default' : undefined
    }))
  });
};

// TabsPanelWithStyles is a wrapper that applies the panelStyles to the TabsPanel component
// It needs to be a function component because it needs to use the useMapTokenPropsToCSS hook
function TabsPanelWithStyles(props) {
  const {
      panelStyles,
      $fullHeightPanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded3);
  const cssStyles = useMapTokenPropsToCSS(panelStyles);
  return /*#__PURE__*/_jsx(TabsPanel, Object.assign({}, rest, {
    $cssStyles: cssStyles,
    $fullHeightPanel: $fullHeightPanel
  }));
}
// bottom CSS property aligns with the border width
const StyledWrapper = styled.div.withConfig({
  displayName: "Tabs__StyledWrapper",
  componentId: "sc-1b1rx27-1"
})(["", ";", ";"], ({
  use,
  bordered,
  theme
}) => isContained(use) ? css(["position:relative;&::before{border-top:", " solid ", ";bottom:-", ";content:'';left:0;position:absolute;width:100%;}"], theme.borderWidth['100'], bordered ? theme.color['border-core-subtle'] : theme.color['fill-transparent-default'], theme.borderWidth['100']) : null, ({
  $cssStyles
}) => $cssStyles);
function StyledWrapperWithStyles(props) {
  var _tabBarContext$tabsSt;
  const {
      tokenProps,
      children
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded4);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const tabBarContext = useTabBarContext();
  const mergedStyles = deepMerge(cssStyles, (_tabBarContext$tabsSt = tabBarContext === null || tabBarContext === void 0 ? void 0 : tabBarContext.tabsStyleOverrides) !== null && _tabBarContext$tabsSt !== void 0 ? _tabBarContext$tabsSt : {});
  return /*#__PURE__*/_jsx(StyledWrapper, Object.assign({}, rest, {
    $cssStyles: mergedStyles,
    children: children
  }));
}
// The block-size calculation accounts for the padding of the Tab Panel.
const StyledTabs = styled.div.withConfig({
  displayName: "Tabs__StyledTabs",
  componentId: "sc-1b1rx27-2"
})(["max-width:100%;", ";", ";"], ({
  use,
  theme
}) => use === 'header' ? css(["margin-bottom:40px;& > ", "{padding-left:", ";}"], StyledSection, theme.space['1100']) : null, ({
  $fullHeightPanel,
  theme
}) => $fullHeightPanel ? css(["height:100%;block-size:calc(100% - ", ");"], theme.space['1200']) : null);
const renderExcessTabsDropdown = (moreTabsRenderer, childArray, excessTabCount, use, handleTabClick, fill) => {
  if (moreTabsRenderer == null) return null;
  const excessTabs = childArray.slice(childArray.length - excessTabCount);
  const excessTabListItems = excessTabs.map(child => {
    var _onClick;
    let onClick;
    if ( /*#__PURE__*/isValidElement(child) && child.props) {
      ({
        onClick
      } = child.props);
    }
    const safeOnClick = (_onClick = onClick) !== null && _onClick !== void 0 ? _onClick : () => {};
    return /*#__PURE__*/cloneElement(child, {
      onClick: memoizedSequence(handleTabClick, safeOnClick),
      active: false,
      children: null,
      role: 'button',
      tooltipPlacement: undefined
    });
  });
  return /*#__PURE__*/cloneElement(moreTabsRenderer({
    tabs: excessTabListItems,
    use,
    fill
  }), {
    'data-more-tabs': true
  });
};

// Define DEFAULT_MORE_TABS_RENDERER here, after imports and before the class
const DEFAULT_MORE_TABS_RENDERER = ({
  tabs,
  use,
  fill
}) => {
  const isBorderRequired = isContained(use);
  const buttonStyles = Object.assign({}, isBorderRequired ? {
    borderInlineStartWidth: '100',
    borderInlineStartColor: 'border-core-subtle',
    borderRadius: '0'
  } : {}, use === 'enclosed' ? {
    borderStartEndRadius: '200'
  } : {}, !fill ? {
    inlineSize: 'auto'
  } : {}, {
    paddingBlock: '300',
    paddingInline: '700',
    flexShrink: 0,
    backgroundColor: getSelectedTabBackgroundColor(use)
  });
  return /*#__PURE__*/_jsx(Dropdown, {
    "aria-hidden": true,
    buttonText: I18n.text('salesUI.UITabs.moreDropdownLabel'),
    buttonUse: "transparent",
    buttonStyles: buttonStyles,
    "data-dropdown-tabs": tabs.length,
    placement: "bottom left",
    responsive: false,
    tabIndex: -1,
    children: /*#__PURE__*/_jsx(List, {
      children: tabs.map(tab => /*#__PURE__*/isValidElement(tab) ? /*#__PURE__*/_jsx(ListItem, {
        children: tab
      }, tab.props.tabId) : null)
    })
  });
};
class TabsComponent extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      excessTabCount: 0
    };
    this._outerEl = null;
    this._measureEl = null;
    this._resizeObserver = null;
    this.checkFit = () => {
      const {
        excessTabCount
      } = this.state;
      const {
        moreTabsRenderer: moreTabsRendererFromProps
      } = this.props;

      // If moreTabsRenderer is null, don't hide any tabs - show all of them
      if (moreTabsRendererFromProps === null) {
        if (excessTabCount !== 0) this.setState({
          excessTabCount: 0
        });
        return;
      }
      const measureEl = this._measureEl;
      const outerEl = this._outerEl;
      const parentEl = outerEl ? outerEl.parentElement : null;
      if (measureEl == null || outerEl == null || parentEl == null) {
        return;
      }
      const containerWidth = Math.ceil(getAvailableWidth(parentEl, outerEl));
      let totalTabsWidth = 0;
      const measureTabsArr = [...measureEl.querySelectorAll('[data-tab-id]')];
      measureTabsArr.forEach(tabEl => {
        if (tabEl instanceof HTMLElement) {
          totalTabsWidth += getElementWidth(tabEl);
        }
      });
      if (totalTabsWidth <= containerWidth) {
        if (excessTabCount !== 0) this.setState({
          excessTabCount: 0
        });
        return;
      }

      // At this point, we know we can't fit all the tabs. How many do we relegate to the dropdown?
      // We start with the "More" dropdown and the active tab...
      const moreTabsEl = measureEl.querySelector('[data-more-tabs]');
      let visibleContentWidth = moreTabsEl ? getElementWidth(moreTabsEl) : 0;
      const activeTab = measureTabsArr.find(el => el.getAttribute('data-tab-selected') === 'true');
      if (activeTab && activeTab instanceof HTMLElement) {
        visibleContentWidth += getElementWidth(activeTab);
      }

      // ...Then we add as many inactive tabs (from left to right) as we can fit.
      const inactiveTabEls = measureTabsArr.filter(el => el.getAttribute('data-tab-selected') !== 'true');
      let newExcessTabCount = inactiveTabEls.length;
      for (let i = 0; i < inactiveTabEls.length; i++) {
        const el = inactiveTabEls[i];
        if (el instanceof HTMLElement) {
          const tabWidth = getElementWidth(el);
          if (visibleContentWidth + tabWidth > containerWidth) break;
          visibleContentWidth += tabWidth;
          newExcessTabCount -= 1;
        }
      }
      if (newExcessTabCount !== excessTabCount) {
        this.setState({
          excessTabCount: newExcessTabCount
        });
      }
    };
  }
  componentDidMount() {
    if (this._outerEl && this._outerEl.parentElement && this._measureEl) {
      this._resizeObserver = new ResizeObserver(this.checkFit);
      this._resizeObserver.observe(this._outerEl.parentElement);
      this._resizeObserver.observe(this._measureEl);
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.selected !== this.props.selected) {
      // ⚠️ This condition prevents infinite loops! ⚠️
      if (prevState.excessTabCount === this.state.excessTabCount) {
        this.checkFit();
      }
    }
    if (prevProps.children !== this.props.children) {
      this.checkFit();
    }
  }
  componentWillUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  }
  render() {
    const _this$props = this.props,
      {
        bordered = true,
        children,
        fill = false,
        fullHeightPanel = false,
        moreTabsRenderer: moreTabsRendererFromProps,
        onSelectedChange,
        selected,
        TabsList = StyledListWithStyles,
        use,
        Wrapper = StyledWrapperWithStyles,
        hideBorders,
        panelStyles = {}
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded5);
    const {
      excessTabCount
    } = this.state;
    const childArray = Children.toArray(children).filter(isValidElement);

    // If the selected tab would be in the excess tab dropdown, move it to
    // first position instead.
    if (excessTabCount > 0) {
      for (let i = childArray.length - excessTabCount; i < childArray.length; i++) {
        const child = childArray[i];
        if ( /*#__PURE__*/isValidElement(child)) {
          const tabChild = child;
          if (tabChild.props.tabId === selected) {
            const firstChild = childArray[0];
            childArray[0] = childArray[i];
            childArray[i] = firstChild;
            break;
          }
        }
      }
    }
    const activeTabPanel = getActiveTabPanel(selected, childArray);
    const componentRole = activeTabPanel ? 'tablist' : 'navigation';
    const safeOnSelectedChange = onSelectedChange !== null && onSelectedChange !== void 0 ? onSelectedChange : () => {};

    // Use the prop if provided, otherwise fall back to the global default
    const fallbackMoreTabsRenderer = moreTabsRendererFromProps === null ? null : moreTabsRendererFromProps || DEFAULT_MORE_TABS_RENDERER;
    const excessTabsDropdown = renderExcessTabsDropdown(fallbackMoreTabsRenderer, childArray, excessTabCount, use, safeOnSelectedChange, fill);
    const tabs = childArray.map((child, index) => {
      const {
        active,
        tabId,
        onClick
      } = child.props;
      const hidden = index >= childArray.length - excessTabCount;
      const tabRole = activeTabPanel ? 'tab' : 'button';
      const safeOnClick = onClick !== null && onClick !== void 0 ? onClick : () => {};
      const tabElement = /*#__PURE__*/cloneElement(child, {
        onClick: memoizedSequence(safeOnSelectedChange, safeOnClick),
        active: active != null ? active : tabId === selected,
        'aria-current': !activeTabPanel && tabId === selected ? 'page' : null,
        'aria-selected': activeTabPanel && tabId === selected ? true : null,
        children: null,
        role: tabRole,
        use,
        fill,
        'aria-hidden': hidden,
        'data-tab-hidden': hidden
      });
      const key = tabId || `tab-provider-${index}`;
      return /*#__PURE__*/_jsx(TabsProvider, {
        tabsSectionStyleOverrides: {}
        // Handles sr-only-focusable functionality
        ,
        tabsLinkStyleOverrides: hidden ? {
          '&:not(:focus)': {
            position: 'absolute',
            pointerEvents: 'none',
            top: '0',
            clip: 'rect(0, 0, 0, 0)'
          }
        } : {},
        tabsTabStyleOverrides: fill ? {
          textAlign: 'center',
          // This is to ensure that the tabs take up the full width of the container
          // We are intentionally not using width: 100% because it was causing a flickering issue (https://git.hubteam.com/HubSpot/foundations-components/pull/7311)
          flex: 1
        } : {},
        children: tabElement
      }, key);
    });
    const measureChildren = tabs.map(tab => {
      return /*#__PURE__*/cloneElement(tab, {
        tooltip: undefined
      });
    });
    const {
      domProps,
      tokenProps
    } = filterProps(rest);
    return /*#__PURE__*/_jsxs(StyledTabs, Object.assign({
      ref: ref => {
        this._outerEl = ref;
      },
      use: use,
      $fullHeightPanel: fullHeightPanel
    }, domProps, rest, {
      children: [/*#__PURE__*/_jsxs(Wrapper, {
        bordered: bordered,
        use: use,
        tokenProps: tokenProps,
        children: [/*#__PURE__*/_jsxs(TabsList, {
          bordered: bordered,
          use: use,
          hideBorders: hideBorders,
          fullWidth: fill,
          "data-content": true,
          role: componentRole,
          children: [tabs, excessTabCount > 0 && excessTabsDropdown]
        }), /*#__PURE__*/_jsx(HiddenMeasure, {
          ref: ref => {
            this._measureEl = ref;
          },
          style: {
            height: 0
          },
          children: /*#__PURE__*/_jsxs(TabsList, {
            bordered: bordered,
            use: use,
            children: [measureChildren, excessTabsDropdown]
          })
        })]
      }), activeTabPanel ? /*#__PURE__*/_jsx(TabsPanelWithStyles, {
        bordered: bordered,
        role: "tabpanel",
        use: use,
        panelStyles: panelStyles,
        $fullHeightPanel: fullHeightPanel,
        children: /*#__PURE__*/_jsx(TabsProvider, {
          children: activeTabPanel
        })
      }) : null]
    }));
  }
}
export const Tabs = Controllable(TabsComponent, ['selected']);
Tabs.displayName = 'Tabs';